/**
 * Whether the composer may send right now, and what to say when it may not.
 *
 * Extracted rather than inlined into ChatPanel so it can be tested the way everything else here is — as
 * plain logic, since there are no render tests in this repo.
 *
 * The case this exists for: after an upload the app starts an auto-analysis turn on its own, which runs for
 * minutes. The composer stayed enabled throughout, so a user with nothing to do typed — and the server,
 * whose session write lock the turn holds, refused the message. The UI knew a turn was in flight the whole
 * time; it just did not say so.
 */

import { isStageLocked, stageLockReason } from "./stages";

/** Placeholder text, so the disabled state explains itself instead of just being dead. */
export function composerPlaceholder({ isConnected, isThinking, stage, hasSourceData = true }) {
  if (!isConnected) return "Reconnecting…";
  if (isThinking) return "The assistant is working — this will unlock when it finishes";
  // Named per stage, and it has to be: on a brand-new session every other stage is disabled until a file
  // exists, so "go to Preview & transform" would point at a tab the user cannot click. Without a reason
  // here the page just reads as broken.
  const locked = stageLockReason(stage, { hasSourceData });
  if (locked) return locked;
  return "Ask anything about your data mappings…";
}

/**
 * A turn in flight blocks a send. So does a read-only stage.
 *
 * Deliberately NOT the only guard against the busy-session error, and it cannot be: `isThinking` lives in
 * this client, so a page reload mid-turn produces a fresh client that believes the session is idle while
 * the server's turn keeps running. The server waits for the lock and reports the real holder; this only
 * stops the common case, where the user is looking straight at the thinking indicator.
 *
 * `stage` gates the OTHER half: changing data is something you do on Preview & transform, so the composer
 * is closed on Upload & view and on Map & review (see ./stages for why those two). Omitting `stage`
 * leaves the send allowed — a caller that does not know the stage must not silently disable the chat.
 */
export function canSendMessage({ isConnected, isThinking, text, stage }) {
  return (
    Boolean(isConnected) &&
    !isThinking &&
    !isStageLocked(stage) &&
    String(text ?? "").trim().length > 0
  );
}
